import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { UPSERT_INCOME_FORM_ID } from '../../components/UpsertIncomeModal/UpsertIncomeModal.meta';
import { IncomeData } from '../../useIncome';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { IncomeModel } from '../../api/useIncomeQuery';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { WorkflowComponentProps } from '../../../../Workflow/Workflow.meta';
import { RealEstateEmpty } from '../../tables/RealEstate/RealEstate.empty';
import { useRealEstateForm } from './useRealEstateForm';
import { AssetBucket } from '../components/AssetBucket/AssetBucket';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';

export interface Props {
  clientId: string;
  income: IncomeModel | null;
  data: IncomeData;
  onSuccess: () => void;
  workflowProps?: WorkflowComponentProps;
}

export const RealEstateForm = ({ clientId, income, data, onSuccess, workflowProps }: Props) => {
  const {
    data: {
      formBag: { control, setValue, formState },
      defaultShowAdvanced,
      mutationBag,
      managementStyleValue,
    },
    actions: { onSubmit },
  } = useRealEstateForm({ clientId, income, onSuccess, data });

  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);

  const { Options: options } = data;
  const { RealEstateAssetOptions } = options;

  if (RealEstateAssetOptions.length === 0) {
    return <RealEstateEmpty clientId={clientId} workflowProps={workflowProps} />;
  }

  return (
    <form
      onSubmit={onSubmit}
      id={UPSERT_INCOME_FORM_ID}
      css={`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
      aria-label="Real Estate income form"
    >
      {mutationBag.error && (
        <Alert
          variant="error"
          message={mutationBag.error.message}
          onRequestClose={mutationBag.reset}
        />
      )}

      <Controller
        name="associatedAssetId"
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            label="Real Estate Asset"
            value={RealEstateAssetOptions.find(({ value: v }) => v === value)}
            options={RealEstateAssetOptions}
            onChange={option => onChange(option ? option.value : null)}
            formVariant={formState.errors.associatedAssetId ? 'error' : undefined}
            helperText={formState.errors.associatedAssetId?.message}
            isPortal
          />
        )}
      />

      <Controller
        control={control}
        name="amount"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            label="Pre-tax Profit"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            innerText="/year"
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            formVariant={formState.errors.amount ? 'error' : undefined}
            helperText={formState.errors.amount?.message}
            {...field}
          />
        )}
      />

      <ManagementStyle
        control={control}
        options={options}
        fieldName={'orionManagementStyleId'}
        onChange={(value: number | null) => {
          setValue('customBucketIndex', value);
        }}
      />

      <AssetBucket
        control={control}
        fieldName="customBucketIndex"
        managementStyleValue={managementStyleValue}
        options={options}
      />

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Controller
            control={control}
            name="incomeGrowth"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Income Growth"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip='Enter the income growth rate applied to the Pre-tax Profit field. Leave this field blank to use the global "Income Growth Rate" found in Settings.'
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name="yearlyTaxableAmount"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Taxable Income"
                decimalScale={2}
                allowNegative={false}
                leftIcon="far fa-dollar-sign"
                innerText="/year"
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip="Enter a taxable amount if the amount to be taxed is different from the yearly amount."
                {...field}
              />
            )}
          />
        </>
      )}
    </form>
  );
};
